import React, { useMemo, useState } from "react";
import {
    ConfigProvider,
    theme as antdTheme,
    Table,
    Checkbox,
    Avatar,
    Tag,
    Switch,
    Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { Moon, Sun, PenLine, FileText, Download, Settings, TagIcon, FolderOutput } from "lucide-react";

import Layout from "../../Layout";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";


/**
 * SignatureListTable
 * ------------------------------------------------------------------
 * Bảng danh sách "Chữ ký" — build bằng Ant Design + lucide-react.
 * - Hỗ trợ Dark mode (antd theme algorithm + class "dark" cho wrapper)
 * - Responsive: bảng cuộn ngang trên mobile (scroll.x), ẩn bớt cột phụ
 *   ở màn hình nhỏ qua responsive breakpoints của antd Column.
 *
 * Cách dùng:
 *   <SignatureListTable />
 *
 * Yêu cầu cài đặt:
 *   npm i antd lucide-react
 * ------------------------------------------------------------------
 */

type Status = "active" | "inactive" | "pending";

interface SignatureRow {
    key: string;
    name: string;
    sharedBy?: string;
    creator: {
        label: string; // ký tự đại diện hiển thị trong avatar
        color: string; // màu nền avatar
        avatarUrl?: string; // nếu có ảnh thật (vd. row "Phú Thiên Hưng" dùng ảnh)
    };
    provider: string; // "Office"
    status: Status;
    mainSignature?: string; // mô tả / hình chữ ký chính (giả lập svg)
    draftSignature?: string; // chữ ký nháy
    users?: { color: string; label: string }[]; // avatar nhóm người dùng
    extraUsersCount?: number;
    createdAt: string;
}

const tabsList = [
    { name: 'Tất cả (119)' },
    { name: 'Công cộng cá nhân (12)', },
    { name: 'Chữ ký số công ty chia sẻ (0)', },
    { name: 'Chuyên dùng nội bộ (107)', },
];

const STATUS_CONFIG: Record<Status, { label: string; color: string; bg: string }> = {
    active: { label: "Đang hoạt động", color: "#15803d", bg: "#dcfce7" },
    inactive: { label: "Dừng hoạt động", color: "#b91c1c", bg: "#fee2e2" },
    pending: { label: "Chưa kích hoạt", color: "#b45309", bg: "#fef3c7" },
};

const DATA: SignatureRow[] = [
    {
        key: "1",
        name: "MAI DUNG",
        creator: { label: "D", color: "#f59e0b" },
        provider: "Office",
        status: "inactive",
        mainSignature: "wavy",
        draftSignature: "small",
        createdAt: "12:10 11/09/2026",
    },
    {
        key: "2",
        name: "NGUYỄN TRINH",
        creator: { label: "T", color: "#2563eb" },
        provider: "Office",
        status: "inactive",
        createdAt: "12:03 11/09/2026",
    },
    {
        key: "3",
        name: "Mạc Quế Hân1",
        creator: { label: "H", color: "#84cc16" },
        provider: "Office",
        status: "active",
        mainSignature: "wavy",
        createdAt: "12:40 06/09/2026",
    },
    {
        key: "4",
        name: "Nguyễn Thị Tố Uyên",
        creator: { label: "U", color: "#9ca3af" },
        provider: "Office",
        status: "active",
        mainSignature: "wavy",
        createdAt: "15:18 12/08/2026",
    },
    {
        key: "5",
        name: "ccc",
        creator: { label: "C", color: "#6366f1" },
        provider: "Office",
        status: "inactive",
        mainSignature: "wavy",
        createdAt: "22:34 04/08/2026",
    },
    {
        key: "6",
        name: "ĐẶNG THỊ NGỌC HÒA",
        creator: { label: "H", color: "#84cc16" },
        provider: "Office",
        status: "active",
        mainSignature: "wavy",
        createdAt: "11:36 28/07/2026",
    },
    {
        key: "7",
        name: "Nguyễn Thị Ngân",
        sharedBy: "N",
        creator: { label: "", color: "#dc2626" }, // icon office = người tạo hệ thống
        provider: "Office",
        status: "active",
        mainSignature: "wavy",
        draftSignature: "small",
        createdAt: "11:44 09/04/2026",
    },
    {
        key: "8",
        name: "Phú Thiên Hưng 01",
        creator: { label: "H", color: "#84cc16" },
        provider: "Office",
        status: "active",
        createdAt: "16:22 08/04/2026",
    },
    {
        key: "9",
        name: "Nguyễn Thị Ngân",
        sharedBy: "N",
        creator: { label: "T", color: "#2563eb" },
        provider: "Office",
        status: "active",
        mainSignature: "wavy",
        createdAt: "17:17 24/02/2026",
    },
    {
        key: "10",
        name: "Nguyễn Thị Ngân",
        sharedBy: "N",
        creator: { label: "", color: "#111827", avatarUrl: "photo" },
        provider: "Office",
        status: "active",
        mainSignature: "wavy",
        draftSignature: "small",
        createdAt: "17:58 29/01/2026",
    },
    {
        key: "11",
        name: "bbb",
        creator: { label: "B", color: "#9ca3af" },
        provider: "Office",
        status: "pending",
        mainSignature: "wavy",
        createdAt: "09:25 20/01/2026",
    },
    {
        key: "12",
        name: "Nguyễn Thị Ngân",
        creator: { label: "N", color: "#16a34a" },
        provider: "Office",
        status: "active",
        mainSignature: "wavy",
        draftSignature: "small",
        users: [{ color: "#dc2626", label: "office" }, { color: "#2563eb", label: "T" }],
        extraUsersCount: 1,
        createdAt: "22:31 15/01/2026",
    },
];

/** Ô chữ ký giả lập — vẽ một nét chữ ký nguệch ngoạc bằng SVG. */
const SignaturePreview: React.FC<{ size?: "main" | "draft" }> = ({ size = "main" }) => {
    const w = size === "main" ? 90 : 56;
    const h = size === "main" ? 40 : 32;
    return (
        <div
            className=" inline-flex items-center justify-center rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-[#ccc]"
            style={{ width: w, height: h }}
        >
            <img src="data:image/png;base64,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" alt="" />
        </div>
    );
};

const EmptyCell: React.FC = () => (
    <span className="text-slate-300 dark:text-slate-600">--</span>
);

const ProviderCell: React.FC<{ provider: string }> = ({ provider }) => (
    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
        <FileText size={15} className="text-rose-500" />
        {provider}
    </span>
);

const CreatorCell: React.FC<{ creator: SignatureRow["creator"] }> = ({ creator }) => {
    if (creator.avatarUrl) {
        return (
            <Avatar size={26} style={{ backgroundColor: "#111827" }}>
                <span className="text-[11px]">👤</span>
            </Avatar>
        );
    }
    if (!creator.label) {
        return (
            <Avatar size={26} style={{ backgroundColor: "#dc2626" }} icon={<FileText size={13} />} />
        );
    }
    return (
        <Avatar size={26} style={{ backgroundColor: creator.color, fontSize: 12 }}>
            {creator.label}
        </Avatar>
    );
};

const StatusTag: React.FC<{ status: Status }> = ({ status }) => {
    const cfg = STATUS_CONFIG[status];
    return (
        <Tag
            style={{
                color: cfg.color,
                background: cfg.bg,
                border: "none",
                padding: "1px 10px",
                fontSize: 12,
            }}
        >
            {cfg.label}
        </Tag>
    );
};

export interface SignatureListTableProps {
    data?: SignatureRow[];
}

const DigitalSignature: React.FC<SignatureListTableProps> = ({ data = DATA }) => {
    const [activeTab, setActiveTab] = useState('Tất cả (119)');
    const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

    const columns: ColumnsType<SignatureRow> = useMemo(
        () => [
            {
                title: (
                    <Checkbox
                        checked={selectedKeys.length === data.length && data.length > 0}
                        indeterminate={selectedKeys.length > 0 && selectedKeys.length < data.length}
                        onChange={(e) =>
                            setSelectedKeys(e.target.checked ? data.map((d) => d.key) : [])
                        }
                    />
                ),
                dataIndex: "select",
                width: 44,
                fixed: "left" as const,
                render: (_: unknown, row) => (
                    <Checkbox
                        checked={selectedKeys.includes(row.key)}
                        onChange={(e) => {
                            setSelectedKeys((prev) =>
                                e.target.checked ? [...prev, row.key] : prev.filter((k) => k !== row.key)
                            );
                        }}
                    />
                ),
            },
            {
                title: "Tên chữ ký",
                dataIndex: "name",
                fixed: "left" as const,
                width: 200,
                render: (v: string) => (
                    <Link to='/digital-signature/detail' className="text-slate-800 dark:text-slate-100">{v}</Link>
                ),
            },
            {
                title: "Được chia sẻ bởi",
                dataIndex: "sharedBy",
                width: 150,
                responsive: ["lg"],
                render: (v?: string) =>
                    v ? (
                        <Avatar size={24} style={{ backgroundColor: "#15803d", fontSize: 11 }}>
                            {v}
                        </Avatar>
                    ) : (
                        <EmptyCell />
                    ),
            },
            {
                title: "Người tạo",
                dataIndex: "creator",
                width: 110,
                render: (creator: SignatureRow["creator"]) => <CreatorCell creator={creator} />,
            },
            {
                title: "Nhà cung cấp",
                dataIndex: "provider",
                width: 130,
                responsive: ["md"],
                render: (v: string) => <ProviderCell provider={v} />,
            },
            {
                title: "Trạng thái",
                dataIndex: "status",
                width: 150,
                render: (v: Status) => <StatusTag status={v} />,
            },
            {
                title: "Chữ ký chính",
                dataIndex: "mainSignature",
                width: 120,
                responsive: ["sm"],
                render: (v?: string) => (v ? <SignaturePreview size="main" /> : <EmptyCell />),
            },
            {
                title: "Chữ ký nháy",
                dataIndex: "draftSignature",
                width: 110,
                responsive: ["lg"],
                render: (v?: string) => (v ? <SignaturePreview size="draft" /> : <EmptyCell />),
            },
            {
                title: "Người sử dụng",
                dataIndex: "users",
                width: 130,
                responsive: ["xl"],
                render: (users: SignatureRow["users"], row) =>
                    users && users.length ? (
                        <Avatar.Group max={{ count: 2 }}>
                            {users.map((u, i) => (
                                <Avatar key={i} style={{ backgroundColor: u.color }}>
                                    {u.label === "office" ? <FileText size={12} /> : u.label}
                                </Avatar>
                            ))}
                            {row.extraUsersCount ? (
                                <Avatar>
                                    +{row.extraUsersCount}
                                </Avatar>
                            ) : null}
                        </Avatar.Group>
                    ) : (
                        <EmptyCell />
                    ),
            },
            {
                title: "Ngày tạo",
                dataIndex: "createdAt",
                width: 150,
                align: "right" as const,
                render: (v: string) => (
                    <span className="text-slate-500 dark:text-slate-400 text-sm">{v}</span>
                ),
            },
        ],
        [data.length, selectedKeys]
    );

    return (
        <ConfigProvider
        >
            <Layout>
                <Helmet>
                    <title>Danh sách ký số | Khothi</title>
                </Helmet>

                {/* HEADER */}
                <div className="flex gap-2 mb-4 justify-between items-center">
                    <h2 className="text-lg text-gray-900 font-semibold dark:text-white">
                        Danh sách ký số
                    </h2>
                </div>
                <div className="bg-white dark:bg-gray-900 mb-4 text-sm">
                    <div className="flex items-center px-4 border-b border-gray-200 dark:border-gray-800 overflow-x-auto space-x-6 scrollbar-none">
                        {tabsList.map(tab => (
                            <button
                                key={tab.name}
                                onClick={() => setActiveTab(tab.name)}
                                className={`py-2.5 font-medium whitespace-nowrap border-b-2 transition-colors shrink-0 ${activeTab === tab.name
                                    ? 'border-[#3e91ab] text-[#3e91ab]'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                    }`}
                            >
                                {tab.name}
                                {/* <span className="text-gray-400 dark:text-gray-500 font-normal">({tab.count})</span> */}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center px-4 py-2 gap-2 bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400">
                        {/* <span className="text-gray-600 dark:text-gray-400">Hiển thị 1 - {filteredProjects.length} / {filteredProjects.length} bản ghi</span> */}
                        <div className="flex items-center space-x-4 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                            <button className="flex items-center hover:text-[#3e91ab] dark:hover:text-[#5bc0de] shrink-0"><FolderOutput size={13} className="mr-1" /> Export</button>
                            <button className="flex items-center hover:text-[#3e91ab] dark:hover:text-[#5bc0de] shrink-0"><Settings size={13} className="mr-1" /> Cài đặt</button>
                        </div>
                    </div>
                </div>

                <Table<SignatureRow>
                    columns={columns}
                    dataSource={data}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: 1100 }}
                    size="middle"
                    rowKey="key"
                />
            </Layout>
        </ConfigProvider>
    );
};

export default DigitalSignature;